/**
 * Section descriptor for a subsection scoped to a `data-pdf-section` marker
 * within a multi-section analytics metrics component (Savings / Pay / Rewards /
 * Loans / Education). Generalises the per-tab `sectionFor` and the capture
 * host's `markerSection` into one builder so a component's subsections have a
 * single source of truth.
 *
 * The returned descriptor is a superset consumed two ways:
 *   - the per-tab download modal (AnalyticsSubPage) reads `getExportTable` for
 *     XLSX/CSV export and `hasData` for the section picker;
 *   - the homepage "Download all" capture (AllAnalyticsCapture) reads
 *     `getContainerEl` / `getPdfData` to screenshot the live DOM.
 * Each consumer picks the fields it needs.
 *
 * @param {import('vue').Ref} componentRef  Template ref to the metrics component.
 * @param {Object} config
 * @param {string} config.key            Section key + `data-pdf-section` marker value.
 * @param {string} config.label          Human-readable section title.
 * @param {string} config.hasDataKey     Exposed boolean flag gating selectability.
 * @param {string} config.exportTableKey Exposed `() => { headers, rows }` for XLSX/CSV.
 * @param {string} [config.pdfDataKey]   Exposed `() => SheetExtras` for PDF-only extras.
 */
export function markerSection(componentRef, { key, label, hasDataKey, exportTableKey, pdfDataKey }) {
  return {
    key,
    label,
    inlineKpis: true,
    hasData: () => !!componentRef.value?.[hasDataKey],
    getContainerEl: () =>
      componentRef.value?.$el?.querySelector(`[data-pdf-section="${key}"]`) || componentRef.value?.$el,
    getExportTable: () => componentRef.value?.[exportTableKey]?.() ?? null,
    ...(pdfDataKey ? { getPdfData: () => componentRef.value?.[pdfDataKey]?.() ?? {} } : {}),
  };
}

/**
 * Section descriptor for a metrics component that is itself a single section
 * (Adoption, App engagement, Track, Budget, Stream Impact, ...). Used by the
 * "Download all" capture host and by the homepage, which threads its own
 * already-rendered sections into the capture.
 *
 * @param {import('vue').Ref} componentRef  Template ref to the metrics component.
 * @param {string} label                    Human-readable section title.
 * @param {Object} [extra]                  Extra descriptor fields (e.g. getPdfData).
 */
export function wholeSection(componentRef, label, extra = {}) {
  return {
    label,
    inlineKpis: true,
    hasData: () => !!componentRef.value?.hasData,
    getContainerEl: () => componentRef.value?.$el,
    ...extra,
  };
}
